import {
	RatesFormSchema,
	type RatesFormValues,
} from '@/features/rules/rates/zod';
import { FormButtons } from '@/pages/rules/components/form-buttons/form-buttons';
import { RuleTabs } from '@/pages/rules/components/rule-tabs/rule-tabs';
import RouterPromptModal from 'components/RouterPromptModal/RouterPromptModal';
import { TwoPaneLayout } from 'components/two-pane-layout/two-pane-layout';
import { alertAdded } from 'features/alerts/redux/alerts-slice';
import { useUpdatePropSeasonRateMutation } from 'features/rules/rates/gql/_gen_/prop-rates.gql';
import {
	seasonChanged,
	selectCurrentSeason,
} from 'features/rules/redux/rules-slice';
import { useUser } from 'features/users/context/userContext';
import { Form, Formik } from 'formik';
import { OccupancyType, PricingStrategy, ProxyType } from 'graphql/gql-types';
import { useApolloError } from 'hooks/useApolloError';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { RatesForm } from './components/rates-form';
import { RatesInfo } from './components/rates-info';

export const Rates = () => {
	const history = useHistory();
	const dispatch = useAppDispatch();
	const { handleApolloError } = useApolloError();
	const { user } = useUser();
	const selectedSeason = useAppSelector(selectCurrentSeason);
	const [updateSeasonRates, { loading: updateRatesLoading }] =
		useUpdatePropSeasonRateMutation();

	const [continueClicked, setContinueClicked] = useState(false);

	const initialValues: RatesFormValues = {
		pricingStrategy:
			selectedSeason?.pricing_strategy ?? PricingStrategy.neutral,
		minRate: selectedSeason?.min_rate?.toFixed(2) ?? '0.00',
		maxRate: selectedSeason?.max_rate?.toFixed(2) ?? '0.00',
		derivedRateType: selectedSeason?.derived_rate_type ?? OccupancyType.single,
		derivedRateOffsetSign:
			(selectedSeason?.derived_rate_offset ?? 0) < 0 ? '-' : '+',
		derivedRateOffset: Math.abs(
			selectedSeason?.derived_rate_offset ?? 0,
		)?.toFixed(2),
		proxy_type: selectedSeason?.proxy_type ?? ProxyType.value,
		proxy_comp_id: selectedSeason?.proxy_comp_id ?? 0,
		proxy_offset_amt:
			selectedSeason?.proxy_type === ProxyType.percent
				? (selectedSeason?.proxy_offset_amt! * 100).toFixed(2)
				: (selectedSeason?.proxy_offset_amt?.toFixed(2) ?? '0.00'),
		use_proxy: selectedSeason?.use_proxy ?? false,
	};

	const handleSubmit = async (values: RatesFormValues) => {
		if (!selectedSeason) return false;

		const parsedValues = RatesFormSchema.parse(values);

		const updatedFields = {
			property_id: selectedSeason.property_id!,
			season_id: selectedSeason.season_id!,
			pricing_strategy: parsedValues.pricingStrategy,
			min_rate: Number.parseFloat(parsedValues.minRate),
			max_rate: Number.parseFloat(parsedValues.maxRate),
			derived_rate_type: parsedValues.derivedRateType,
			derived_rate_offset:
				(parsedValues.derivedRateOffsetSign === '+' ? 1 : -1) *
				Number.parseFloat(parsedValues.derivedRateOffset),
			last_updated_by: user?.login_id,
			proxy_type: parsedValues.proxy_type,
			proxy_comp_id: parsedValues.proxy_comp_id || undefined,
			proxy_offset_amt:
				parsedValues.proxy_type === ProxyType.value
					? Number.parseFloat(parsedValues.proxy_offset_amt)
					: Number.parseFloat(parsedValues.proxy_offset_amt) / 100,
			use_proxy: parsedValues.use_proxy,
		};

		const { data } = await updateSeasonRates({
			variables: {
				updatedPropSeasonRates: updatedFields,
			},
			refetchQueries: ['GetPropSeasons'],
			onCompleted: () => {
				dispatch(seasonChanged({ ...selectedSeason, ...updatedFields }));
			},
			onError: (error) => {
				handleApolloError(error);
			},
		});

		if (data?.updatePropertySeasonRates?.success) {
			dispatch(alertAdded('success', data.updatePropertySeasonRates.message!));
			if (continueClicked) history.push('/rules/autopilot');
		} else {
			dispatch(alertAdded('error', 'Rate changes not saved'));
			setContinueClicked(false);
		}
	};

	return (
		<div>
			<RuleTabs loading={updateRatesLoading} />
			<Formik<RatesFormValues>
				initialValues={initialValues}
				enableReinitialize
				onSubmit={handleSubmit}
			>
				{({ dirty, validateForm, submitForm }) => (
					<>
						<RouterPromptModal isBlocked={dirty && !continueClicked} />
						<Form>
							<TwoPaneLayout
								loading={updateRatesLoading}
								LeftSideComponent={<RatesForm />}
								RightSideComponent={<RatesInfo />}
							/>
							<FormButtons
								loading={updateRatesLoading}
								dirty={dirty}
								onContinueClick={async () => {
									if (!dirty) {
										history.push('/rules/autopilot');
										return;
									}
									const isValid =
										Object.keys(await validateForm()).length === 0;
									if (isValid) setContinueClicked(true);
									submitForm();
								}}
							/>
						</Form>
					</>
				)}
			</Formik>
		</div>
	);
};
